import { SessionPlace, UndoEntry } from './types';
import { SourceEvent } from '../events/types';
import { RootState } from '@state/types';

// Before each Undo: is what the change touched still as the change left it? If not, the Undo is refused ("it has
// changed since") rather than overwriting what came after.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const toSecond = (ms: number) => Math.floor(ms / 1000);

/**
 * An event as the server would compare it: keys in one order, null the same as missing, and dates to the second
 * whether they came as text or as dates. The events reload and the edit's own answer hold the same record written
 * differently ("2026-10-24T09:00:00.000Z" against "2026-10-24T09:00:00Z"), and comparing them as text said
 * "changed since" when nothing had.
 */
const normalise = (value: unknown): unknown => {
  if (value === null || value === undefined) return undefined;
  if (value instanceof Date) return toSecond(value.getTime());
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? value : toSecond(ms);
  }
  if (Array.isArray(value)) return value.map(normalise);
  if (typeof value === 'object') {
    const object = value as Record<string, unknown>;
    return Object.keys(object)
      .sort()
      .reduce<Record<string, unknown>>((normalised, key) => {
        const field = normalise(object[key]);
        if (field !== undefined) normalised[key] = field;
        return normalised;
      }, {});
  }
  return value;
};

export const sameEvent = (a?: SourceEvent, b?: SourceEvent) =>
  a === b || (!!a && !!b && JSON.stringify(normalise(a)) === JSON.stringify(normalise(b)));

/**
 * An event delete or edit is as it left things when every record it saved is still as saved, and the event it started
 * from is either one of them or gone (the whole event deleted, or a series replaced by a new one).
 */
export const eventsStillAsLeft = (before: SourceEvent, after: SourceEvent[], sourceEvents: SourceEvent[]) => {
  const current = new Map(sourceEvents.map(event => [event.id, event]));
  return (
    after.every(saved => sameEvent(current.get(saved.id), saved)) &&
    (after.some(saved => saved.id === before.id) || !current.has(before.id))
  );
};

const sameInstant = (a: string | Date | undefined, b: string | Date | undefined) =>
  !!a && !!b && toSecond(new Date(a).getTime()) === toSecond(new Date(b).getTime());

/**
 * A moved session is as the move left it while it sits where the move put it and is ticked (or not) as it was. Not on
 * the calendar (a week not loaded, or unplanned since): the server has the last word, answering 404 once it's gone.
 */
export const sessionStillAt = (
  to: SessionPlace,
  isUsed: boolean,
  current: { start: string | Date; end: string | Date; isUsed?: boolean } | undefined
) => !current || (sameInstant(current.start, to.start) && sameInstant(current.end, to.end) && !!current.isUsed === isUsed);

// A study session as the calendar holds it, with the time logged on it.
type HeldSession = { taskId?: number; isUsed?: boolean; used?: number };

const heldSession = (state: RootState, id: number) => state.calendar.plannedEvents[id] as HeldSession | undefined;

/**
 * Whether a change is still as it left things, so taking it back undoes only it. Every kind is listed, so a new one
 * (the timer's, Bumpers') doesn't build until it says how it is checked.
 */
export const stillAsLeft = (state: RootState, entry: UndoEntry): boolean => {
  switch (entry.kind) {
    case 'sessionMoved':
      return sessionStillAt(entry.to, entry.isUsed, state.calendar.plannedEvents[entry.to.id]);
    case 'sessionUsed': {
      // Ticked or not as the change left it, with the same minutes logged.
      const current = heldSession(state, entry.plannedTaskId);
      return !current || (!!current.isUsed === entry.marked && (current.used ?? 0) === entry.after.used);
    }
    case 'event':
    case 'eventEdited':
      return eventsStillAsLeft(entry.before, entry.after, state.event.sourceEvents);
    case 'createdEvent': {
      // Gone already: the server says so (404).
      const current = state.event.sourceEvents.find(event => event.id === entry.event.id);
      return !current || sameEvent(current, entry.event);
    }
    case 'createdTasks': {
      // Undoing a creation deletes for good: not once time has been logged on it.
      const sessions = Object.values(state.calendar.plannedEvents) as HeldSession[];
      return !sessions.some(
        session => entry.taskIds.includes(Number(session.taskId)) && (session.isUsed || (session.used ?? 0) > 0)
      );
    }
    case 'listOrder': {
      const task = state.workload.tasks.find(({ id }) => id === entry.taskId) as { listOrder?: number | null } | undefined;
      return entry.order === undefined || !task || task.listOrder === entry.order;
    }
    // Cleared by the timer's Reset: still with nothing logged and no tick.
    case 'timerReset':
    case 'sessionCleared': {
      const current = heldSession(state, entry.plannedTaskId);
      return !current || (!current.isUsed && (current.used ?? 0) === 0);
    }
    // Moved by the timer (▶ bumping the day, Keep going) or by Bumpers: each still where it put them, unticked.
    case 'timerStartRearranged':
    case 'sessionsBumped':
      return (entry.to || []).every(place => sessionStillAt(place, false, state.calendar.plannedEvents[place.id]));
    // A task dropped with Bumpers on: the sessions it bumped, still where they went. Otherwise the server leaves alone
    // what changed since: tasks restored or reopened, sessions marked used.
    case 'planned':
      return (entry.to || []).every(place => sessionStillAt(place, false, state.calendar.plannedEvents[place.id]));
    case 'tasks':
      return true;
  }
};
